import { format } from 'date-fns';
import { useStore } from 'hooks/useStore';
import { Job } from 'Interfaces/JobsInterface';
import { ArrowRight, Check, Edit, X } from 'lucide-react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useRef, useState, useMemo } from 'react';
import classes from './NoteTemplateWithIcon.module.scss';
import { useNoteTemplate } from './useNoteTemplate';


const NoteTemplateWithIcon = observer((rowData: Job) => {
  const { UserStore, JobPasteStore } = useStore();
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const op = useRef<OverlayPanel>(null);

  const { addNote, updateNote, isSubmitting, isUpdating } = useNoteTemplate(rowData.jobId);

  const sortedNotes = useMemo(() => {
    const notes = JobPasteStore.getJobNotes(rowData.jobId) || rowData.notes || [];
    // Sort in reverse chronological order (newest first) so override notes appear at top
    return [...notes].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA; // Newest first
    });
  }, [JobPasteStore.jobs, JobPasteStore._cloned_jobs_for_edits, rowData.notes, rowData.jobId]);

  const handleAddNote = async () => {
    if (!newNote.trim() || isSubmitting) {
      return;
    }

    const currentUser = UserStore.userMetadata?.userInfo.name;
    await addNote(newNote, currentUser || 'system');
    setNewNote('');
  };

  const handleStartEdit = (noteId: number, text: string) => {
    setEditingNoteId(noteId);
    setEditText(text);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
  };

  const handleSaveEdit = async (noteId: number) => {
    if (!editText.trim() || isUpdating) {
      return;
    }

    const currentUser = UserStore.userMetadata?.userInfo.name;
    await updateNote(noteId, editText, currentUser || 'system');
    setEditingNoteId(null);
  };

  const showNotes = (event: React.MouseEvent) => {
    op.current?.toggle(event);
  };

  // Get avatar color class based on initials
  const getAvatarColorClass = (initials: string) => {
    const colors: Record<string, string> = {
      BS: classes.purple,
      AT: classes.red,
      RA: classes.green
    };
    return colors[initials] || classes.purple;
  };

  // Extract initials from user name or email
  const getInitials = (user: string) => {
    if (!user) {
      return 'UN';
    }

    const parts = user.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.substring(0, 2).toUpperCase();
  };

  let displayText = 'No notes';
  if (sortedNotes.length > 0) {
    const latestNote = sortedNotes[0]; // First note is now the newest
    const maxLength = 20;
    const noteText = latestNote.noteText;
    displayText = noteText.length > maxLength ? `${noteText.substring(0, maxLength)}...` : noteText;
  }

  return (
    <div className={classes.noteWithIcon}>
      <span onClick={showNotes}>{displayText}</span>
      {/* <span onClick={isEditMode ? () => { } : showNotes}>{displayText}</span> */}
      <OverlayPanel
        ref={op}
        className={classes.notesOverlay}
        style={{ width: '400px' }}
        showCloseIcon
      >
        <div className={classes.notesHeader}>
          <h3>Notes</h3>
        </div>
        <div className={classes.notesContainer}>
          <div className={classes.addNoteForm}>
            <InputText
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Note..."
              className={classes.addNoteInput}
            />
            <Button
              label="Submit"
              icon={!isSubmitting && <ArrowRight size={16} />}
              iconPos="right"
              onClick={handleAddNote}
              className={classes.addNoteButton}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </div>
          <div className={classes.notesList}>
            {sortedNotes.length === 0 ? (
              <div className={classes.emptyNotes}>No notes available for this job</div>
            ) : (
              sortedNotes.map(note => {
                const noteId = note.id;
                const isEditing = editingNoteId === noteId;
                const initials = getInitials(note.createdBy || '');
                const isNoteCreator =
                  (note.createdBy || '').toLowerCase() ===
                  UserStore.userMetadata?.userInfo.name.toLowerCase();

                return (
                  <div key={noteId} className={classes.noteItem}>
                    <div className={classes.noteHeader}>
                      <div className={`${classes.noteAvatar} ${getAvatarColorClass(initials)}`}>
                        {initials}
                      </div>
                      <div className={classes.noteContent}>
                        {isEditing ? (
                          <InputText
                            value={editText}
                            onChange={e => setEditText(e.target.value)}
                            className={classes.editingNote}
                            autoFocus
                          />
                        ) : (
                          <div className={classes.noteText}>{note.noteText}</div>
                        )}
                        <div className={classes.noteDate}>
                          {format(new Date(note.created_at), 'MM/dd/yyyy hh:mm a')}
                        </div>
                        <div className={isEditing ? classes.editActions : classes.noteActions}>
                          {isEditing ? (
                            <>
                              <button
                                className={classes.saveEditButton}
                                onClick={() => handleSaveEdit(noteId)}
                                disabled={isUpdating}
                              >
                                <Check size={16} />
                              </button>
                              <button
                                className={classes.cancelEditButton}
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            isNoteCreator && (
                              <button
                                className={classes.editNoteButton}
                                onClick={() => handleStartEdit(noteId, note.noteText)}
                              >
                                <Edit size={16} />
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
});

export default NoteTemplateWithIcon;
